"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Ban, Loader2 } from "lucide-react";
import {
  CropIcon,
  EcomButton,
  EcomModal,
  EcomPopover,
  EcomRadioSelect,
  EcomSwitch as Switch,
  EcomTabs,
  useEcomFeedback,
  useEcomPollingFeedback,
} from "../../ui";
import {
  ImageUploadAddTrigger,
  ImageUploadEmptyState,
  ImageUploadField,
  type ImageUploadFieldItem,
} from "../ui/image-upload-field";
import {
  ecomImageApi,
  executeFunctional,
  executeFunctionalBatch,
  getFunctionalResult as requestFunctionalResult,
  type ComponentConfig,
  type MyCostsResult,
  type WorkflowCostItem,
  type WorkflowUIConfig,
} from "../../lib/ecom-image-api";
import { calculateEcomFixedBillingEstimate } from "../../lib/ecom-fixed-billing-estimate";
import { getCollapsedRatioSummary } from "../../lib/ecom-auto-ratio";
import { normalizeReversePromptToPlainText } from "../../lib/ecom-reverse-prompt-plain-text";
import {
  buildDetailReplicaReverseBatches,
  clearDetailReplicaReverseHistory,
  createDetailReplicaReverseSignature,
  createDetailReplicaReverseScreenKeys,
  createDetailReplicaReverseTaskOwners,
  DETAIL_REPLICA_BLANK_FLAGS_KEY,
  DETAIL_REPLICA_CROP_SIZES_KEY,
  DETAIL_REPLICA_FONT_COLOR_KEY,
  DETAIL_REPLICA_MAX_SCREENS,
  DETAIL_REPLICA_RATIO_CONFIG_SIGNATURE_KEY,
  DETAIL_REPLICA_IMAGE_STYLE_KEY,
  DETAIL_REPLICA_RATIOS_KEY,
  loadDetailReplicaReverseHistory,
  resolveDetailReplicaComponentKeys,
  resolveDetailReplicaGate,
  resolveDetailReplicaRatioConfigSignature,
  resolveDetailReplicaRatioOptions,
  resolveDetailReplicaRatioResolutions,
  resolveDetailReplicaReversePollingDecision,
  resolveDetailReplicaState,
  saveDetailReplicaReverseHistory,
  type DetailReplicaReverseRow,
  type DetailReplicaReverseTaskOwner,
} from "../../lib/ecom-detail-page-replica";
import { IMAGE_ACCEPT, MAX_REVERSE_PROMPT_BYTES, isImageFile } from "../../lib/file-utils";
import { cn } from "../../lib/utils";
import { AutoResizeTextarea } from "./AutoResizeTextarea";
import DetailPageReplicaConfigSteps from "./DetailPageReplicaConfigSteps";
import ImageCropDialog from "./ImageCropDialog";
import { createClientUUID } from "../../lib/client-uuid";
import {
  claimReversePromptOperation,
  getLatestReversePromptOperation,
  isReversePromptOperationCurrent,
  subscribeReversePromptOperations,
  updateReversePromptOperation,
  type ReversePromptOperationClaim,
} from "../../lib/ecom-reverse-prompt-lifecycle";

const PROMPT_STYLE_OPTIONS = [
  { value: "standard", label: "标准版" },
  { value: "remove_text", label: "去文字版" },
] as const;

type HsvColor = {
  h: number;
  s: number;
  v: number;
};

function clampColorChannel(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function normalizeHexColor(value: string, fallback = "#000000"): string {
  const normalized = value.trim().toUpperCase();
  return /^#[0-9A-F]{6}$/.test(normalized) ? normalized : fallback;
}

function hexToHsv(value: string): HsvColor {
  const hex = normalizeHexColor(value).slice(1);
  const red = Number.parseInt(hex.slice(0, 2), 16) / 255;
  const green = Number.parseInt(hex.slice(2, 4), 16) / 255;
  const blue = Number.parseInt(hex.slice(4, 6), 16) / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const delta = max - min;
  let hue = 0;

  if (delta > 0) {
    if (max === red) hue = 60 * (((green - blue) / delta) % 6);
    else if (max === green) hue = 60 * ((blue - red) / delta + 2);
    else hue = 60 * ((red - green) / delta + 4);
  }

  return {
    h: hue < 0 ? hue + 360 : hue,
    s: max === 0 ? 0 : delta / max,
    v: max,
  };
}

function hsvToHex({ h, s, v }: HsvColor): string {
  const hue = ((h % 360) + 360) % 360;
  const saturation = clampColorChannel(s);
  const value = clampColorChannel(v);
  const chroma = value * saturation;
  const segment = hue / 60;
  const secondary = chroma * (1 - Math.abs((segment % 2) - 1));
  let red = 0;
  let green = 0;
  let blue = 0;

  if (segment < 1) [red, green] = [chroma, secondary];
  else if (segment < 2) [red, green] = [secondary, chroma];
  else if (segment < 3) [green, blue] = [chroma, secondary];
  else if (segment < 4) [green, blue] = [secondary, chroma];
  else if (segment < 5) [red, blue] = [secondary, chroma];
  else [red, blue] = [chroma, secondary];

  const match = value - chroma;
  return `#${[red, green, blue]
    .map((channel) => Math.round((channel + match) * 255).toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase()}`;
}

function HsvColorPicker({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const hsv = hexToHsv(value);

  const updateSaturation = (event: ReactPointerEvent<HTMLDivElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const saturation = clampColorChannel((event.clientX - bounds.left) / bounds.width);
    const brightness = clampColorChannel(1 - (event.clientY - bounds.top) / bounds.height);
    onChange(hsvToHex({ h: hsv.h, s: saturation, v: brightness }));
  };

  const updateHue = (event: ReactPointerEvent<HTMLDivElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const hue = clampColorChannel((event.clientX - bounds.left) / bounds.width) * 360;
    onChange(hsvToHex({ ...hsv, h: hue === 360 ? 0 : hue }));
  };

  const handlePointerDown = (
    event: ReactPointerEvent<HTMLDivElement>,
    update: (pointerEvent: ReactPointerEvent<HTMLDivElement>) => void,
  ) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    update(event);
  };

  const handlePointerMove = (
    event: ReactPointerEvent<HTMLDivElement>,
    update: (pointerEvent: ReactPointerEvent<HTMLDivElement>) => void,
  ) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) update(event);
  };

  return (
    <div data-detail-replica-color-picker className="w-full select-none">
      <div
        data-detail-replica-color-saturation
        role="slider"
        tabIndex={0}
        aria-label="调整字体颜色饱和度和明度"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(hsv.v * 100)}
        aria-valuetext={normalizeHexColor(value)}
        className="relative aspect-square w-full cursor-crosshair touch-none overflow-hidden rounded-[3px]"
        style={{ backgroundColor: `hsl(${hsv.h} 100% 50%)` }}
        onPointerDown={(event) => handlePointerDown(event, updateSaturation)}
        onPointerMove={(event) => handlePointerMove(event, updateSaturation)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onChange(normalizeHexColor(value));
            return;
          }
          const step = event.shiftKey ? 0.05 : 0.01;
          if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
          event.preventDefault();
          const saturation = hsv.s + (event.key === "ArrowLeft" ? -step : event.key === "ArrowRight" ? step : 0);
          const brightness = hsv.v + (event.key === "ArrowDown" ? -step : event.key === "ArrowUp" ? step : 0);
          onChange(hsvToHex({ ...hsv, s: saturation, v: brightness }));
        }}
      >
        <span className="pointer-events-none absolute inset-0 bg-gradient-to-r from-white to-transparent" />
        <span className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent to-black" />
        <span
          data-detail-replica-color-saturation-marker
          className="pointer-events-none absolute h-[14px] w-[14px] -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-white shadow-[0_1px_3px_rgba(0,0,0,.8)]"
          style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%` }}
        />
      </div>
      <div
        data-detail-replica-color-hue
        role="slider"
        tabIndex={0}
        aria-label="调整字体颜色色相"
        aria-valuemin={0}
        aria-valuemax={360}
        aria-valuenow={Math.round(hsv.h)}
        className="relative mt-2 h-3 cursor-pointer touch-none rounded-full"
        style={{
          background: "linear-gradient(90deg, #FF0000 0%, #FFFF00 16.67%, #00FF00 33.33%, #00FFFF 50%, #0000FF 66.67%, #FF00FF 83.33%, #FF0000 100%)",
        }}
        onPointerDown={(event) => handlePointerDown(event, updateHue)}
        onPointerMove={(event) => handlePointerMove(event, updateHue)}
        onKeyDown={(event) => {
          if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
          event.preventDefault();
          const step = event.shiftKey ? 10 : 1;
          onChange(hsvToHex({ ...hsv, h: hsv.h + (event.key === "ArrowLeft" ? -step : step) }));
        }}
      >
        <span
          data-detail-replica-color-hue-marker
          className="pointer-events-none absolute top-1/2 h-[14px] w-[14px] -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-white bg-transparent shadow-[0_1px_3px_rgba(0,0,0,.8)]"
          style={{ left: `${(hsv.h / 360) * 100}%` }}
        />
      </div>
    </div>
  );
}

type ReverseRow = DetailReplicaReverseRow;

type DetailPageReplicaConfigPanelProps = {
  workflowKey: string;
  callableKey?: string;
  uiConfig: WorkflowUIConfig;
  params: Record<string, unknown>;
  onParamChange: (key: string, value: unknown) => void;
  activeIndex: number;
  onActiveIndexChange: (index: number) => void;
  onUploadingChange?: (uploading: boolean) => void;
  onPromptGeneratingChange?: (generating: boolean) => void;
  onImageHoverPreviewChange?: (preview: { url: string; alt?: string } | null) => void;
  onReversePromptCompleted?: () => void;
  onGenerate?: (batchCount?: number) => void;
  billingCosts?: WorkflowCostItem[];
  myCosts?: MyCostsResult | null;
  reversePromptHistoryScope?: string;
  configTitleAction?: ReactNode;
  renderConfigTitle?: (title: ReactNode) => ReactNode;
};

function getUploadedURL(data: unknown): string {
  if (!data || typeof data !== "object") return "";
  const value = data as Record<string, unknown>;
  for (const key of ["file_url", "public_url", "url"]) {
    if (typeof value[key] === "string" && value[key].trim()) return value[key].trim();
  }
  return "";
}

function resolveReverseTemplateID(component?: ComponentConfig): number | undefined {
  const config = component?.config;
  for (const key of ["multi_template_id", "single_template_id", "inspire_template_id"]) {
    const value = Number(config?.[key]);
    if (Number.isFinite(value) && value > 0) return value;
  }
  return undefined;
}

function resolveReverseCost(
  reverseComponent: ComponentConfig | undefined,
  myCosts?: MyCostsResult | null,
): { cost: number; original: number } {
  const configured = Number(
    reverseComponent?.config?.reverse_cost
      || reverseComponent?.config?.multi_cost,
  );
  const standard = Number.isSafeInteger(configured) && configured > 0 ? configured : 0;
  const discount = myCosts?.tier?.discount_rate;
  const cost = typeof discount === "number" && standard > 0 ? Math.ceil(standard * discount) : standard;
  return { cost, original: standard };
}

export default function DetailPageReplicaConfigPanel({
  workflowKey,
  callableKey,
  uiConfig,
  params,
  onParamChange,
  activeIndex,
  onActiveIndexChange,
  onUploadingChange,
  onPromptGeneratingChange,
  onImageHoverPreviewChange,
  onReversePromptCompleted,
  onGenerate,
  billingCosts = [],
  myCosts,
  reversePromptHistoryScope,
  configTitleAction,
  renderConfigTitle,
}: DetailPageReplicaConfigPanelProps) {
  const feedback = useEcomFeedback();
  const { run: runEcomPollingRequest } = useEcomPollingFeedback();
  const getFunctionalResult = useCallback(
    (taskId: string) => runEcomPollingRequest(
      `ecom-image:functional-result:${taskId}`,
      () => requestFunctionalResult(taskId),
    ),
    [runEcomPollingRequest],
  );
  const keys = useMemo(() => resolveDetailReplicaComponentKeys(uiConfig), [uiConfig]);
  const ratioOptions = useMemo(() => resolveDetailReplicaRatioOptions(uiConfig), [uiConfig]);
  const ratioLabels = useMemo(
    () => Object.fromEntries(ratioOptions.map((option) => [option.value, option.label])),
    [ratioOptions],
  );
  const ratioResolutions = useMemo(
    () => resolveDetailReplicaRatioResolutions(uiConfig),
    [uiConfig],
  );
  const ratioComponent = useMemo(
    () => uiConfig.components.find((component) => (
      component.is_visible && component.component_type === "ratio_selector"
    )),
    [uiConfig.components],
  );
  const state = useMemo(() => resolveDetailReplicaState(uiConfig, params), [params, uiConfig]);
  const gate = useMemo(() => resolveDetailReplicaGate(state), [state]);
  const reverseComponent = useMemo(
    () => uiConfig.components.find((component) => component.coze_param_key === keys.reverse),
    [keys.reverse, uiConfig.components],
  );
  const detailReplicaConfigComponent = useMemo(
    () => uiConfig.components.find((component) => component.component_type === "detail_page_replica_config"),
    [uiConfig.components],
  );
  const configuredTitle = detailReplicaConfigComponent?.config?.detail_replica_config_title
    || reverseComponent?.config?.detail_replica_config_title;
  const configTitle = typeof configuredTitle === "string" && configuredTitle.trim()
    ? configuredTitle.trim()
    : "配置";
  const templateID = resolveReverseTemplateID(reverseComponent);
  const unitCost = resolveReverseCost(reverseComponent, myCosts);
  const safeActiveIndex = Math.max(0, Math.min(activeIndex, Math.max(0, state.references.length - 1)));
  const [uploadingKind, setUploadingKind] = useState<"product" | "model" | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [colorOpen, setColorOpen] = useState(false);
  const [croppingScreenImage, setCroppingScreenImage] = useState<{
    kind: "product" | "model";
    screenIndex: number;
    imageIndex: number;
    imageUrl: string;
  } | null>(null);
  const [reversing, setReversing] = useState(false);
  const [rows, setRows] = useState<ReverseRow[]>([]);
  const [pendingTaskOwners, setPendingTaskOwners] = useState<Record<string, DetailReplicaReverseTaskOwner>>({});
  const pendingTaskOwnersRef = useRef<Record<string, DetailReplicaReverseTaskOwner>>({});
  const [historyReady, setHistoryReady] = useState(false);
  const [reverseLifecycleRevision, setReverseLifecycleRevision] = useState(0);
  const [reverseModalHasBottomOverflow, setReverseModalHasBottomOverflow] = useState(false);
  const reverseModalScrollRef = useRef<HTMLDivElement>(null);
  const rowsReady = rows.length === state.references.length
    && rows.length > 0
    && rows.every((row) => row.status === "completed" && !!row.prompt);
  const hasReverseResults = gate.promptsReady || rowsReady;
  const mountedRef = useRef(true);
  const onParamChangeRef = useRef(onParamChange);
  const onPromptGeneratingChangeRef = useRef(onPromptGeneratingChange);
  const onUploadingChangeRef = useRef(onUploadingChange);
  const rowsRef = useRef<ReverseRow[]>([]);
  const stateRef = useRef(state);
  const activeReverseTaskIDsRef = useRef(new Set<string>());
  const reverseStartedAtRef = useRef(0);
  const loadedHistoryIdentityRef = useRef("");
  const localReverseOperationIDsRef = useRef(new Set<string>());
  const reverseSignature = useMemo(
    () => createDetailReplicaReverseSignature(state, templateID),
    [state, templateID],
  );
  const reverseScreenKeys = useMemo(
    () => createDetailReplicaReverseScreenKeys(state, templateID),
    [state, templateID],
  );
  const reverseLifecycleChannelPrefix = `detail:${workflowKey}:`;
  const reverseLifecycleChannel = `${reverseLifecycleChannelPrefix}${state.imageStyle}`;
  const pendingReverseScreenKeys = useMemo(() => new Set(
    Object.values(pendingTaskOwners).map((owner) => owner.screen_key),
  ), [pendingTaskOwners]);
  onParamChangeRef.current = onParamChange;
  onPromptGeneratingChangeRef.current = onPromptGeneratingChange;
  onUploadingChangeRef.current = onUploadingChange;
  rowsRef.current = rows;
  pendingTaskOwnersRef.current = pendingTaskOwners;
  stateRef.current = state;
  const reverseEstimateCost = unitCost.cost * state.references.length;
  const reverseEstimateOriginal = unitCost.original * state.references.length;
  const generateEstimate = useMemo(() => {
    const billing = billingCosts.find((item) => item.workflow_key === workflowKey && item.enabled);
    if (!billing) return null;

    let billingMode = billing.billing_mode;
    let standardUnitCost = billing.fixed_cost;
    if (billing.match_field && billing.price_rules?.length) {
      const paramValue = String(params[billing.match_field] ?? "");
      const matchedRule = billing.price_rules.find((rule) => rule.match_value === paramValue);
      if (matchedRule) {
        billingMode = matchedRule.billing_mode;
        standardUnitCost = matchedRule.fixed_cost;
      }
    }
    if (billingMode !== "fixed") return null;

    return calculateEcomFixedBillingEstimate({
      standardUnitCost,
      batchCount: state.references.length,
      userCostItem: myCosts?.costs?.find((item) => item.workflow_key === workflowKey),
      discountRate: myCosts?.tier?.discount_rate,
    });
  }, [billingCosts, myCosts, params, state.references.length, workflowKey]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      onPromptGeneratingChangeRef.current?.(false);
      onUploadingChangeRef.current?.(false);
    };
  }, []);

  useEffect(() => subscribeReversePromptOperations(reversePromptHistoryScope, (snapshot) => {
    if (snapshot && !snapshot.channel.startsWith(reverseLifecycleChannelPrefix)) return;
    if (snapshot && localReverseOperationIDsRef.current.has(snapshot.operationId)) return;
    loadedHistoryIdentityRef.current = "";
    setReverseLifecycleRevision((current) => current + 1);
  }), [reverseLifecycleChannelPrefix, reversePromptHistoryScope]);

  useEffect(() => {
    const historyIdentity = JSON.stringify([
      reversePromptHistoryScope || "",
      workflowKey,
      reverseSignature,
      reverseLifecycleRevision,
    ]);
    if (loadedHistoryIdentityRef.current === historyIdentity) return;
    loadedHistoryIdentityRef.current = historyIdentity;

    setHistoryReady(false);
    activeReverseTaskIDsRef.current.clear();
    const initialRows = state.references.map<ReverseRow>((_, index) => ({
      status: state.prompts[index] ? "completed" : "idle",
      progress: state.prompts[index] ? 100 : 0,
      prompt: state.prompts[index] || "",
    }));
    const history = loadDetailReplicaReverseHistory(
      reversePromptHistoryScope,
      workflowKey,
      state.imageStyle,
    );
    const canRestore = history?.signature === reverseSignature
      && history.rows.length === state.references.length;
    if (!canRestore) {
      if (history) {
        clearDetailReplicaReverseHistory(
          reversePromptHistoryScope,
          workflowKey,
          state.imageStyle,
        );
      }
      rowsRef.current = initialRows;
      setRows(initialRows);
      setPendingTaskOwners({});
      reverseStartedAtRef.current = 0;
      setReversing(false);
      onPromptGeneratingChangeRef.current?.(false);
      setHistoryReady(true);
      return;
    }

    const activeOperation = getLatestReversePromptOperation<{
      signature?: string;
      taskOwners?: Record<string, DetailReplicaReverseTaskOwner>;
    }>(reversePromptHistoryScope, reverseLifecycleChannel);
    const isSubmitting = activeOperation?.phase === "submitting"
      && activeOperation.data?.signature === reverseSignature;
    const restoredRows = history.rows.map((row, index) => {
      const taskID = history.task_ids[index];
      const owner = taskID ? history.task_owners?.[taskID] : undefined;
      if (
        row.status === "processing" &&
        !isSubmitting &&
        (!owner || owner.screen_key !== reverseScreenKeys[index])
      ) {
        return { ...row, status: "failed" as const, progress: 0 };
      }
      return row;
    });
    const hasPendingTasks = isSubmitting || restoredRows.some(
      (row, index) => {
        const taskID = history.task_ids[index];
        return row.status === "processing"
          && !!taskID
          && history.task_owners?.[taskID]?.screen_key === reverseScreenKeys[index];
      },
    );
    rowsRef.current = restoredRows;
    setRows(restoredRows);
    setPendingTaskOwners(history.task_owners || {});
    pendingTaskOwnersRef.current = history.task_owners || {};
    reverseStartedAtRef.current = history.started_at;
    setReversing(hasPendingTasks);
    onPromptGeneratingChangeRef.current?.(hasPendingTasks);
    const restoredPrompts = restoredRows.map((row, index) => (
      row.status === "completed" && row.prompt ? row.prompt : state.prompts[index] || ""
    ));
    if (restoredPrompts.some((prompt, index) => prompt !== state.prompts[index])) {
      onParamChangeRef.current(keys.prompt, restoredPrompts);
    }
    setHistoryReady(true);
  }, [
    keys.prompt,
    reversePromptHistoryScope,
    reverseLifecycleChannel,
    reverseLifecycleRevision,
    reverseScreenKeys,
    reverseSignature,
    state.imageStyle,
    state.prompts,
    state.references,
    workflowKey,
  ]);

  useEffect(() => {
    if (!historyReady || rows.length === 0 || rows.length !== state.references.length) return;
    saveDetailReplicaReverseHistory({
      signature: reverseSignature,
      rows,
      task_ids: reverseScreenKeys.map((screenKey) => (
        Object.values(pendingTaskOwners).find((owner) => owner.screen_key === screenKey)?.task_id || ""
      )),
      task_owners: pendingTaskOwners,
      started_at: reverseStartedAtRef.current || Date.now(),
    }, reversePromptHistoryScope, workflowKey, state.imageStyle);
  }, [
    historyReady,
    pendingTaskOwners,
    reversePromptHistoryScope,
    reverseScreenKeys,
    reverseSignature,
    rows,
    state.imageStyle,
    state.references.length,
    workflowKey,
  ]);

  const updateReverseModalOverflow = useCallback(() => {
    const scroll = reverseModalScrollRef.current;
    if (!scroll) return;
    setReverseModalHasBottomOverflow(
      scroll.scrollHeight > scroll.clientHeight + 1
      && scroll.scrollTop + scroll.clientHeight < scroll.scrollHeight - 1,
    );
  }, []);

  useEffect(() => {
    if (!modalOpen) {
      setReverseModalHasBottomOverflow(false);
      return undefined;
    }
    updateReverseModalOverflow();
    const scroll = reverseModalScrollRef.current;
    if (!scroll) return undefined;
    scroll.addEventListener("scroll", updateReverseModalOverflow, { passive: true });
    const observer = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(updateReverseModalOverflow)
      : null;
    observer?.observe(scroll);
    return () => {
      scroll.removeEventListener("scroll", updateReverseModalOverflow);
      observer?.disconnect();
    };
  }, [modalOpen, rows, state.references.length, updateReverseModalOverflow]);

  const updateScreenGroups = useCallback((kind: "product" | "model", index: number, images: string[]) => {
    const source = kind === "product" ? state.products : state.models;
    const key = kind === "product" ? keys.product : keys.model;
    const next = Array.from({ length: state.references.length }, (_, screenIndex) => (
      screenIndex === index ? images : [...(source[screenIndex] || [])]
    ));
    onParamChange(key, next);
    if (state.prompts[index]) {
      const nextPrompts = [...state.prompts];
      nextPrompts[index] = "";
      onParamChange(keys.prompt, nextPrompts);
    }
  }, [keys.model, keys.product, keys.prompt, onParamChange, state.models, state.products, state.prompts, state.references.length]);

  const updateReverseSetting = useCallback((key: string, value: string) => {
    onParamChange(key, value);
    if (state.prompts.some(Boolean)) {
      onParamChange(keys.prompt, state.references.map(() => ""));
    }
  }, [keys.prompt, onParamChange, state.prompts, state.references]);

  const uploadScreenFiles = useCallback(async (kind: "product" | "model", files: FileList | File[]) => {
    const imageFiles = Array.from(files).filter(isImageFile);
    if (imageFiles.length === 0 || uploadingKind) return;
    const current = kind === "product" ? state.products[safeActiveIndex] || [] : state.models[safeActiveIndex] || [];
    const previews = imageFiles.map((file) => URL.createObjectURL(file));
    let workingImages = [...current, ...previews];
    updateScreenGroups(kind, safeActiveIndex, workingImages);
    setUploadingKind(kind);
    onUploadingChange?.(true);
    try {
      for (let index = 0; index < imageFiles.length; index += 1) {
        try {
          const response = kind === "model"
            ? await ecomImageApi.uploadModelImage(imageFiles[index], { maxBytes: MAX_REVERSE_PROMPT_BYTES })
            : await ecomImageApi.uploadProductImage(imageFiles[index], { maxBytes: MAX_REVERSE_PROMPT_BYTES });
          const uploadedURL = getUploadedURL(response.data);
          if (!uploadedURL) throw new Error("E5003");
          workingImages = workingImages.map((url) => url === previews[index] ? uploadedURL : url);
          updateScreenGroups(kind, safeActiveIndex, workingImages);
          URL.revokeObjectURL(previews[index]);
        } catch {
          workingImages = workingImages.filter((url) => url !== previews[index]);
          updateScreenGroups(kind, safeActiveIndex, workingImages);
          URL.revokeObjectURL(previews[index]);
          feedback.error({ code: "E5003", fallback: "operation" });
        }
      }
    } finally {
      setUploadingKind(null);
      onUploadingChange?.(false);
    }
  }, [onUploadingChange, safeActiveIndex, state.models, state.products, updateScreenGroups, uploadingKind]);

  const handleScreenCropSave = useCallback(async (croppedDataUrl: string) => {
    const target = croppingScreenImage;
    setCroppingScreenImage(null);
    if (!target || uploadingKind) return;

    const source = target.kind === "product"
      ? stateRef.current.products
      : stateRef.current.models;
    const currentImages = [...(source[target.screenIndex] || [])];
    if (currentImages[target.imageIndex] !== target.imageUrl) return;

    setUploadingKind(target.kind);
    onUploadingChange?.(true);
    try {
      const blob = await fetch(croppedDataUrl).then((response) => response.blob());
      const file = new File(
        [blob],
        `detail-replica-${target.kind}-cropped-${Date.now()}.png`,
        { type: "image/png" },
      );
      const response = target.kind === "model"
        ? await ecomImageApi.uploadModelImage(file, { maxBytes: MAX_REVERSE_PROMPT_BYTES })
        : await ecomImageApi.uploadProductImage(file, { maxBytes: MAX_REVERSE_PROMPT_BYTES });
      const uploadedURL = getUploadedURL(response.data);
      if (!uploadedURL) throw new Error("E5003");
      currentImages[target.imageIndex] = uploadedURL;
      updateScreenGroups(target.kind, target.screenIndex, currentImages);
    } catch {
      feedback.error({ code: "E5003", fallback: "operation" });
    } finally {
      setUploadingKind(null);
      onUploadingChange?.(false);
    }
  }, [croppingScreenImage, onUploadingChange, updateScreenGroups, uploadingKind]);

  const setBlank = useCallback((checked: boolean) => {
    const next = Array.from({ length: state.references.length }, (_, index) => (
      index === safeActiveIndex ? checked : state.blankFlags[index] === true
    ));
    onParamChange(DETAIL_REPLICA_BLANK_FLAGS_KEY, next);
    if (checked) updateScreenGroups("product", safeActiveIndex, []);
  }, [onParamChange, safeActiveIndex, state.blankFlags, state.references.length, updateScreenGroups]);

  const setRatio = useCallback((ratio: string, cropSize: string | null) => {
    const nextRatios = Array.from({ length: state.references.length }, (_, index) => (
      index === safeActiveIndex ? ratio : state.ratios[index] || ratio
    ));
    const nextCropSizes = Array.from({ length: state.references.length }, (_, index) => (
      index === safeActiveIndex ? cropSize || "" : state.cropSizes[index] || ""
    ));
    onParamChange(DETAIL_REPLICA_RATIOS_KEY, nextRatios);
    onParamChange(DETAIL_REPLICA_CROP_SIZES_KEY, nextCropSizes);
    onParamChange(keys.ratio, ratio);
    onParamChange("__output_crop_size", cropSize);
    onParamChange(
      DETAIL_REPLICA_RATIO_CONFIG_SIGNATURE_KEY,
      resolveDetailReplicaRatioConfigSignature(uiConfig),
    );
  }, [
    keys.ratio,
    onParamChange,
    safeActiveIndex,
    state.cropSizes,
    state.ratios,
    state.references.length,
    uiConfig,
  ]);

  const updateRow = useCallback((index: number, patch: Partial<ReverseRow>) => {
    setRows((current) => {
      const next = current.map((row, rowIndex) => rowIndex === index ? { ...row, ...patch } : row);
      rowsRef.current = next;
      return next;
    });
  }, []);

  const updateReversePromptRow = useCallback((index: number, prompt: string) => {
    const nextRows = rowsRef.current.map((row, rowIndex) => (
      rowIndex === index && row.status === "completed"
        ? { ...row, prompt }
        : row
    ));
    rowsRef.current = nextRows;
    setRows(nextRows);
    const nextPrompts = stateRef.current.references.map((_, rowIndex) => (
      nextRows[rowIndex]?.status === "completed"
        ? nextRows[rowIndex].prompt
        : stateRef.current.prompts[rowIndex] || ""
    ));
    onParamChangeRef.current(keys.prompt, nextPrompts);
  }, [keys.prompt]);

  const pollTask = useCallback(async (
    owner: DetailReplicaReverseTaskOwner,
    operation: ReversePromptOperationClaim,
  ): Promise<string> => {
    let progress = 12;
    while (
      isReversePromptOperationCurrent(operation) &&
      activeReverseTaskIDsRef.current.has(owner.task_id)
    ) {
      let result: Awaited<ReturnType<typeof getFunctionalResult>>["data"];
      try {
        const response = await getFunctionalResult(owner.task_id);
        result = response.data;
      } catch {
        await new Promise((resolve) => setTimeout(resolve, 1500));
        continue;
      }
      if (
        !isReversePromptOperationCurrent(operation) ||
        !activeReverseTaskIDsRef.current.has(owner.task_id)
      ) return "";
      const index = reverseScreenKeys.indexOf(owner.screen_key);
      if (index < 0) return "";
      const decision = resolveDetailReplicaReversePollingDecision(
        result?.status,
        result?.inspire_prompt,
      );
      if (decision === "completed" && result?.inspire_prompt) {
        const prompt = normalizeReversePromptToPlainText(result.inspire_prompt);
        if (mountedRef.current) updateRow(index, { status: "completed", progress: 100, prompt });
        return prompt;
      }
      if (decision === "failed") {
        throw new Error("E5302");
      }
      progress = Math.min(94, progress + Math.max(1, Math.round(Math.random() * 8)));
      if (mountedRef.current) updateRow(index, { status: "processing", progress });
      await new Promise((resolve) => setTimeout(resolve, 1500));
    }
    return "";
  }, [reverseScreenKeys, updateRow]);

  const completeReverseTasks = useCallback(async (
    taskOwners: Record<string, DetailReplicaReverseTaskOwner>,
    operation: ReversePromptOperationClaim,
  ) => {
    if (!isReversePromptOperationCurrent(operation)) return;
    localReverseOperationIDsRef.current.add(operation.operationId);
    const owners = Object.values(taskOwners);
    owners.forEach((owner) => activeReverseTaskIDsRef.current.add(owner.task_id));
    const settled = await Promise.allSettled(owners.map((owner) => pollTask(owner, operation)));
    if (!isReversePromptOperationCurrent(operation)) return;

    const settledByScreen = new Map(owners.map((owner, ownerIndex) => [owner.screen_key, settled[ownerIndex]]));
    const nextRows = rowsRef.current.map((row, index) => {
      const result = settledByScreen.get(reverseScreenKeys[index]);
      if (!result) return row;
      return result?.status === "fulfilled" && result.value
        ? { status: "completed" as const, progress: 100, prompt: result.value }
        : { status: "failed" as const, progress: 0, prompt: "" };
    });
    const nextPrompts = [...stateRef.current.prompts];
    nextRows.forEach((row, index) => {
      if (settledByScreen.has(reverseScreenKeys[index])) {
        nextPrompts[index] = row.status === "completed" ? row.prompt : "";
      }
    });
    const ownedIndexes = reverseScreenKeys.flatMap((screenKey, index) => (
      settledByScreen.has(screenKey) ? [index] : []
    ));
    const hadFailure = ownedIndexes.some((index) => nextRows[index]?.status !== "completed");
    const hadSuccess = ownedIndexes.some((index) => nextRows[index]?.status === "completed");

    rowsRef.current = nextRows;
    if (mountedRef.current) setRows(nextRows);
    const completedTaskIDs = new Set(owners.map((owner) => owner.task_id));
    owners.forEach((owner) => activeReverseTaskIDsRef.current.delete(owner.task_id));
    const remaining = Object.fromEntries(
      Object.entries(pendingTaskOwnersRef.current).filter(([taskID]) => !completedTaskIDs.has(taskID)),
    );
    pendingTaskOwnersRef.current = remaining;
    const stillReversing = Object.keys(remaining).length > 0;
    if (mountedRef.current) {
      setPendingTaskOwners(remaining);
      setReversing(stillReversing);
      onPromptGeneratingChange?.(stillReversing);
    }
    saveDetailReplicaReverseHistory({
      signature: reverseSignature,
      rows: nextRows,
      task_ids: reverseScreenKeys.map((screenKey) => (
        Object.values(remaining).find((owner) => owner.screen_key === screenKey)?.task_id || ""
      )),
      task_owners: remaining,
      started_at: reverseStartedAtRef.current || Date.now(),
    }, reversePromptHistoryScope, workflowKey, stateRef.current.imageStyle);
    onParamChange(keys.prompt, nextPrompts);
    if (hadSuccess) onReversePromptCompleted?.();
    updateReversePromptOperation(operation, hadFailure && !hadSuccess ? "failed" : "completed", {
      signature: reverseSignature,
      rows: nextRows,
      errorCode: hadFailure ? "E5302" : undefined,
    });
    localReverseOperationIDsRef.current.delete(operation.operationId);
    if (hadFailure && mountedRef.current) feedback.error({ code: "E5302", fallback: "operation" });
  }, [
    keys.prompt,
    onParamChange,
    onPromptGeneratingChange,
    onReversePromptCompleted,
    pollTask,
    reversePromptHistoryScope,
    reverseScreenKeys,
    reverseSignature,
    workflowKey,
  ]);

  useEffect(() => {
    if (!historyReady) return;
    Object.values(pendingTaskOwners).forEach((owner) => {
      if (activeReverseTaskIDsRef.current.has(owner.task_id)) return;
      const latestOperation = getLatestReversePromptOperation(
        reversePromptHistoryScope,
        reverseLifecycleChannel,
      );
      const operation = latestOperation
        ? claimReversePromptOperation(
            reversePromptHistoryScope,
            latestOperation.channel,
            latestOperation.operationId,
            latestOperation.data,
          )
        : claimReversePromptOperation(
            reversePromptHistoryScope,
            reverseLifecycleChannel,
            `restored:${reverseStartedAtRef.current || Date.now()}`,
            { signature: reverseSignature, taskOwners: pendingTaskOwners },
          );
      if (operation) void completeReverseTasks({ [owner.task_id]: owner }, operation);
    });
  }, [
    completeReverseTasks,
    historyReady,
    pendingTaskOwners,
    reverseLifecycleChannel,
    reversePromptHistoryScope,
    reverseSignature,
  ]);

  const runReverseAll = useCallback(async () => {
    // 30 屏上限是后端 /functional/execute-batch 的硬限制，超限提交会整批 400、全部屏
    // 标记失败；这里在提交前全局报错拦截，让用户先删到限额内再反推。
    if (state.references.length > DETAIL_REPLICA_MAX_SCREENS) {
      feedback.error({ code: "E5304", fallback: "operation" });
      return;
    }
    if (!gate.reverseReady) {
      feedback.info("请先完成每一屏的产品图配置");
      return;
    }
    if (!gate.settingsReady) {
      feedback.info("请先配置有效的字体颜色");
      return;
    }

    const operation = claimReversePromptOperation(
      reversePromptHistoryScope,
      reverseLifecycleChannel,
      createClientUUID(),
      { signature: reverseSignature },
    );
    if (!operation) return;
    localReverseOperationIDsRef.current.add(operation.operationId);
    setReversing(true);
    onPromptGeneratingChange?.(true);
    const initialRows = state.references.map<ReverseRow>(() => ({
      status: "processing",
      progress: 8,
      prompt: "",
    }));
    rowsRef.current = initialRows;
    setRows(initialRows);
    setPendingTaskOwners({});
    pendingTaskOwnersRef.current = {};
    reverseStartedAtRef.current = Date.now();
    saveDetailReplicaReverseHistory({
      signature: reverseSignature,
      rows: initialRows,
      task_ids: reverseScreenKeys.map(() => ""),
      task_owners: {},
      started_at: reverseStartedAtRef.current,
    }, reversePromptHistoryScope, workflowKey, state.imageStyle);
    try {
      const batches = buildDetailReplicaReverseBatches(state, templateID);
      const response = await executeFunctionalBatch({
        callable_key: callableKey || workflowKey,
        batches,
        concurrency: Math.min(3, Math.max(1, batches.length)),
      });
      const taskIDs = response.data?.task_ids || [];
      if (taskIDs.length !== batches.length) throw new Error("E5302");
      const taskOwners = createDetailReplicaReverseTaskOwners(taskIDs, reverseScreenKeys);
      if (!isReversePromptOperationCurrent(operation)) return;
      if (Object.keys(taskOwners).length !== batches.length) throw new Error("E5302");
      setPendingTaskOwners(taskOwners);
      pendingTaskOwnersRef.current = taskOwners;
      saveDetailReplicaReverseHistory({
        signature: reverseSignature,
        rows: initialRows,
        task_ids: taskIDs,
        task_owners: taskOwners,
        started_at: reverseStartedAtRef.current,
      }, reversePromptHistoryScope, workflowKey, state.imageStyle);
      updateReversePromptOperation(operation, "polling", {
        signature: reverseSignature,
        taskOwners,
      });
      await completeReverseTasks(taskOwners, operation);
    } catch {
      if (!isReversePromptOperationCurrent(operation)) return;
      const failedRows = rowsRef.current.map((row) => row.status === "completed"
        ? row
        : { ...row, status: "failed" as const, progress: 0 });
      rowsRef.current = failedRows;
      setRows(failedRows);
      setPendingTaskOwners({});
      pendingTaskOwnersRef.current = {};
      setReversing(false);
      onPromptGeneratingChange?.(false);
      saveDetailReplicaReverseHistory({
        signature: reverseSignature,
        rows: failedRows,
        task_ids: [],
        started_at: reverseStartedAtRef.current || Date.now(),
      }, reversePromptHistoryScope, workflowKey, state.imageStyle);
      updateReversePromptOperation(operation, "failed", {
        signature: reverseSignature,
        rows: failedRows,
        errorCode: "E5302",
      });
      localReverseOperationIDsRef.current.delete(operation.operationId);
      feedback.error({ code: "E5302", fallback: "operation" });
    }
  }, [
    callableKey,
    completeReverseTasks,
    gate.reverseReady,
    gate.settingsReady,
    onPromptGeneratingChange,
    reversePromptHistoryScope,
    reverseLifecycleChannel,
    reverseScreenKeys,
    reverseSignature,
    state,
    templateID,
    workflowKey,
  ]);

  const retryRow = useCallback(async (index: number) => {
    if (!gate.settingsReady || Object.values(pendingTaskOwners).some((owner) => owner.screen_key === reverseScreenKeys[index])) return;
    const batch = buildDetailReplicaReverseBatches(state, templateID)[index];
    if (!batch) return;
    const operation = claimReversePromptOperation(
      reversePromptHistoryScope,
      `${reverseLifecycleChannel}:screen:${reverseScreenKeys[index]}`,
      createClientUUID(),
      { signature: reverseSignature, screenKey: reverseScreenKeys[index] },
    );
    if (!operation) return;
    localReverseOperationIDsRef.current.add(operation.operationId);
    setReversing(true);
    updateRow(index, { status: "processing", progress: 8, prompt: "" });
    onPromptGeneratingChange?.(true);
    reverseStartedAtRef.current = Date.now();
    try {
      const response = await executeFunctional({
        callable_key: callableKey || workflowKey,
        params: batch.params,
      });
      const taskID = response.data?.task_id;
      if (!taskID) throw new Error("E5302");
      const taskIDs = Array.from({ length: state.references.length }, (_, rowIndex) => (
        rowIndex === index ? taskID : ""
      ));
      const taskOwners = createDetailReplicaReverseTaskOwners(taskIDs, reverseScreenKeys);
      if (!isReversePromptOperationCurrent(operation)) return;
      const owner = taskOwners[taskID];
      if (!owner || owner.screen_key !== reverseScreenKeys[index]) throw new Error("E5302");
      const mergedTaskOwners = { ...pendingTaskOwnersRef.current, ...taskOwners };
      pendingTaskOwnersRef.current = mergedTaskOwners;
      setPendingTaskOwners(mergedTaskOwners);
      saveDetailReplicaReverseHistory({
        signature: reverseSignature,
        rows: rowsRef.current,
        task_ids: reverseScreenKeys.map((screenKey) => (
          screenKey === owner.screen_key
            ? owner.task_id
            : Object.values(pendingTaskOwners).find((pendingOwner) => pendingOwner.screen_key === screenKey)?.task_id || ""
        )),
        task_owners: mergedTaskOwners,
        started_at: reverseStartedAtRef.current,
      }, reversePromptHistoryScope, workflowKey, state.imageStyle);
      updateReversePromptOperation(operation, "polling", {
        signature: reverseSignature,
        taskOwners,
      });
      await completeReverseTasks(taskOwners, operation);
    } catch {
      if (!isReversePromptOperationCurrent(operation)) return;
      updateRow(index, { status: "failed", progress: 0, prompt: "" });
      const stillReversing = Object.keys(pendingTaskOwners).length > 0;
      setReversing(stillReversing);
      onPromptGeneratingChange?.(stillReversing);
      saveDetailReplicaReverseHistory({
        signature: reverseSignature,
        rows: rowsRef.current,
        task_ids: reverseScreenKeys.map((screenKey) => (
          Object.values(pendingTaskOwners).find((owner) => owner.screen_key === screenKey)?.task_id || ""
        )),
        task_owners: pendingTaskOwners,
        started_at: reverseStartedAtRef.current || Date.now(),
      }, reversePromptHistoryScope, workflowKey, state.imageStyle);
      updateReversePromptOperation(operation, "failed", {
        signature: reverseSignature,
        screenKey: reverseScreenKeys[index],
        errorCode: "E5302",
      });
      localReverseOperationIDsRef.current.delete(operation.operationId);
      feedback.error({ code: "E5302", fallback: "operation" });
    }
  }, [
    callableKey,
    completeReverseTasks,
    gate.settingsReady,
    onPromptGeneratingChange,
    pendingTaskOwners,
    reversePromptHistoryScope,
    reverseLifecycleChannel,
    reverseScreenKeys,
    reverseSignature,
    state,
    templateID,
    updateRow,
    workflowKey,
  ]);

  const openReverseModal = useCallback(() => {
    if (!gate.reverseReady) {
      feedback.warning("请先完成每一屏的产品图配置");
      return;
    }
    setModalOpen(true);
  }, [gate.reverseReady]);

  const closeReverseModal = useCallback(() => {
    setModalOpen(false);
    setColorOpen(false);
  }, []);

  const productImages = state.products[safeActiveIndex] || [];
  const modelImages = state.models[safeActiveIndex] || [];
  const isBlank = state.blankFlags[safeActiveIndex] === true;
  const configTitleNode = (
    <h2
      data-detail-replica-config-position
      aria-live="polite"
      className="mb-3 flex items-center gap-1.5 text-[14px] font-medium"
    >
      <span>{configTitle} ({safeActiveIndex + 1}/{state.references.length})</span>
      {configTitleAction}
    </h2>
  );

  return (
    <>
      <div data-detail-replica-config className="flex min-h-full flex-col text-[#F6F6F6]">
        {renderConfigTitle ? renderConfigTitle(configTitleNode) : configTitleNode}
        <DetailPageReplicaConfigSteps
          count={state.references.length}
          activeIndex={safeActiveIndex}
          completedScreens={gate.completedScreens}
          onChange={onActiveIndexChange}
        />

        <div className="mb-2 flex items-center justify-between">
          <label className="text-[14px] font-medium">上传产品图</label>
          <span className="flex items-center gap-2 text-[13px] text-[#8C8C8C]">
            空白图
            <Switch
              checked={isBlank}
              onCheckedChange={setBlank}
              aria-label="使用空白图"
              variant="flowx"
              size="flowx"
            />
          </span>
        </div>
        <ScreenUploadField
          kind="product"
          images={productImages}
          disabled={isBlank || uploadingKind === "model"}
          uploading={uploadingKind === "product"}
          onFiles={(files) => void uploadScreenFiles("product", files)}
          onRemove={(index) => updateScreenGroups("product", safeActiveIndex, productImages.filter((_, imageIndex) => imageIndex !== index))}
          onReorder={(images) => updateScreenGroups("product", safeActiveIndex, images)}
          onHoverPreviewChange={onImageHoverPreviewChange}
          onEdit={(index) => setCroppingScreenImage({
            kind: "product",
            screenIndex: safeActiveIndex,
            imageIndex: index,
            imageUrl: productImages[index],
          })}
        />

        <label className="mb-2 mt-4 text-[14px] font-medium">上传模特图（可选）</label>
        <ScreenUploadField
          kind="model"
          images={modelImages}
          disabled={uploadingKind === "product"}
          uploading={uploadingKind === "model"}
          onFiles={(files) => void uploadScreenFiles("model", files)}
          onRemove={(index) => updateScreenGroups("model", safeActiveIndex, modelImages.filter((_, imageIndex) => imageIndex !== index))}
          onReorder={(images) => updateScreenGroups("model", safeActiveIndex, images)}
          onHoverPreviewChange={onImageHoverPreviewChange}
          onEdit={(index) => setCroppingScreenImage({
            kind: "model",
            screenIndex: safeActiveIndex,
            imageIndex: index,
            imageUrl: modelImages[index],
          })}
        />

        <div className="mt-4">
          <div className="mb-2 text-[14px] font-medium text-white">
            {ratioComponent?.label || "比例"}
          </div>
          <EcomRadioSelect
            label={ratioComponent?.label || "比例"}
            summary={getCollapsedRatioSummary({
              selectedRatio: state.ratios[safeActiveIndex] || ratioOptions[0]?.value || "auto",
              cropSize: state.cropSizes[safeActiveIndex] || null,
            })}
            ratios={ratioOptions.map((option) => option.value)}
            ratioLabels={ratioLabels}
            selectedRatio={state.ratios[safeActiveIndex] || ratioOptions[0]?.value || "auto"}
            selectedCropSize={state.cropSizes[safeActiveIndex] || null}
            submitMode={state.cropSizes[safeActiveIndex] ? "resolution" : "ratio"}
            ratioResolutions={ratioResolutions}
            autoTitle={
              typeof ratioComponent?.config?.auto_anchor_param_key === "string"
                && ratioComponent.config.auto_anchor_param_key.trim().length > 0
                ? "auto（根据上传图片智能识别比例）"
                : undefined
            }
            onRatioChange={(ratio) => setRatio(ratio, null)}
            onResolutionChange={(ratio, resolution) => setRatio(ratio, resolution)}
            disabled={ratioOptions.length === 0}
          />
        </div>

        <div className="mt-auto pt-10">
          <div className="mb-2 flex items-center justify-between text-[12px] text-[#A0A0A0]">
            <span>预估消耗</span>
            <span>
              {unitCost.cost > 0 && unitCost.cost < unitCost.original && (
                <span className="mr-2 line-through">{reverseEstimateOriginal}</span>
              )}
              <strong
                className="text-[18px] font-medium text-[#FCB453]"
                data-detail-replica-reverse-estimate
              >
                {reverseEstimateCost}
              </strong>
              <span className="ml-1">豌豆</span>
            </span>
          </div>
          <EcomButton
            type="button"
            size="large"
            variant={gate.reverseReady ? "default" : "outline"}
            onClick={openReverseModal}
            aria-busy={reversing}
            data-ready={gate.reverseReady ? "true" : "false"}
            className={cn(!gate.reverseReady && "cursor-not-allowed")}
          >
            {reversing ? (
              <span className="inline-flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                <span>正在反推提示词</span>
              </span>
            ) : "反推提示词"}
          </EcomButton>
        </div>
      </div>

      {croppingScreenImage && (
        <ImageCropDialog
          imageUrl={croppingScreenImage.imageUrl}
          onCancel={() => setCroppingScreenImage(null)}
          onSave={(croppedDataUrl) => void handleScreenCropSave(croppedDataUrl)}
        />
      )}

      <EcomModal
        open={modalOpen}
        onClose={closeReverseModal}
        layer="modal"
        maskClosable
        closeLabel="关闭反推提示词"
        width="min(1200px, calc(100vw - 40px))"
        title={(
          <div className="flex w-full items-center justify-between">
            <span className="text-base font-medium text-white">反推提示词</span>
          </div>
        )}
        classNames={{
          mask: "backdrop-blur-[6px]",
          panel: "flex flex-col overflow-hidden border text-white shadow-2xl ring-0",
          header: "shrink-0",
          title: "w-full",
          body: "flex min-h-0 flex-1 flex-col",
          footer: "shrink-0",
        }}
        styles={{
          mask: {
            background: "rgba(0, 0, 0, 0.55)",
            backdropFilter: "blur(6px)",
          },
          panel: {
            height: "min(730px, calc(100vh - 16px))",
            overflow: "hidden",
            border: "1px solid rgba(255, 255, 255, 0.05)",
            borderRadius: 10,
            background: "#111215",
          },
          header: {
            background: "#111215",
          },
          body: {
            minHeight: 0,
            overflow: "hidden",
          },
          footer: {},
        }}
      >
          <div
            className="relative min-h-0 flex-1"
            data-detail-replica-reverse-modal
          >
            <div
              ref={reverseModalScrollRef}
              className="h-full overflow-y-auto pr-1 [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.2)_transparent] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-white/20 [&::-webkit-scrollbar-thumb:hover]:bg-white/30 [&::-webkit-scrollbar-track]:bg-transparent"
              data-detail-replica-reverse-scroll
            >
              <div className="space-y-6">
                {state.references.map((reference, index) => {
                  // 与 buildDetailReplicaReverseBatches 的图片语义保持一致：
                  // 参考图作为图1，当前屏产品图/模特图依次作为图2、图3、图4……
                  // 空白屏(blankFlags=true)不渲染产品图占位，仅保留参考图。
                  const products = state.blankFlags[index] ? [] : (state.products[index] || []);
                  const models = state.models[index] || [];
                  const screenImages: Array<{ url: string; label: string }> = [
                    { url: reference, label: "图1" },
                    ...products.map((url, productIndex) => ({
                      url,
                      label: `图${productIndex + 2}`,
                    })),
                    ...models.map((url, modelIndex) => ({
                      url,
                      label: `图${products.length + modelIndex + 2}`,
                    })),
                  ];
                  const row = rows[index];
                  const status = row?.status || "idle";
                  return (
                    <div key={`${reference}-${index}`} data-detail-replica-reverse-draft={index}>
                      <div className="flex items-end gap-5" data-detail-replica-reverse-media-actions>
                        <div className="flex min-w-0 flex-1 flex-wrap gap-2">
                          {screenImages.map((image, imageIndex) => (
                            <ModalThumb
                              key={`${image.url}-${imageIndex}`}
                              url={image.url}
                              label={image.label}
                            />
                          ))}
                        </div>
                        <div
                          className="ml-auto flex shrink-0 items-center gap-2 self-end"
                          data-detail-replica-reverse-regenerate-actions={index}
                        >
                            <EcomButton
                              type="button"
                              variant="outline"
                              onClick={() => void retryRow(index)}
                              disabled={pendingReverseScreenKeys.has(reverseScreenKeys[index])}
                              className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-[5px] px-3 text-[13px] disabled:cursor-not-allowed"
                              data-detail-replica-reverse-regenerate={index}
                            >
                              <svg
                                viewBox="0 0 1024 1024"
                                aria-hidden="true"
                                className="h-[13px] w-[13px] shrink-0"
                              >
                                <path
                                  d="M882.801778 523.633778a36.096 36.096 0 0 0-72.163556-1.393778h-0.568889l-0.028444 1.365333a299.264 299.264 0 1 1-47.900445-174.648889h-53.930666a36.067556 36.067556 0 0 0 0 72.163556h138.638222a36.181333 36.181333 0 0 0 36.124444-36.067556V246.357333a36.096 36.096 0 0 0-72.192 0v45.880889a370.915556 370.915556 0 1 0 71.395556 237.966222 23.04 23.04 0 0 0 0.625778-6.599111z m0 0v-0.056889l-1.422222 0.085333h1.422222v-0.028444z"
                                  fill="currentColor"
                                />
                              </svg>
                              重新生成
                            </EcomButton>
                            {unitCost.cost > 0 && (
                              <span className="inline-flex shrink-0 items-baseline gap-1">
                                <span className="text-[18px] leading-none text-[#FCB453]">{unitCost.cost}</span>
                                <span className="text-[13px] leading-none text-white/45">豌豆</span>
                              </span>
                            )}
                        </div>
                      </div>
                      {status === "completed" ? (
                        <AutoResizeTextarea
                          value={row?.prompt || ""}
                          data-ecom-prompt-input
                          onChange={(event) => updateReversePromptRow(index, event.target.value)}
                          minHeight={48}
                          disabled={reversing}
                          aria-label={`第${index + 1}屏提示词`}
                          data-detail-replica-reverse-prompt-editor={index}
                          className="mt-3 w-full rounded-[6px] border border-white/15 bg-transparent px-3 py-2.5 text-[13px] leading-6 text-white/82 outline-none transition-colors focus:border-white/35 disabled:cursor-not-allowed disabled:opacity-70"
                        />
                      ) : status === "failed" ? (
                        <div className="mt-3 rounded-[6px] border border-red-400/20 bg-red-400/5 px-3 py-2.5 text-[13px] leading-6 text-red-100/75">
                          {row?.prompt || "E5302：反推失败，请重新生成"}
                        </div>
                      ) : (
                        <div className="mt-2" data-detail-replica-reverse-pending={index}>
                          <div className="h-1.5 overflow-hidden rounded-full bg-[#555557]">
                            {status === "processing" && (
                              <div
                                role="progressbar"
                                aria-label={`第${index + 1}屏反推进度`}
                                aria-valuemin={0}
                                aria-valuemax={100}
                                aria-valuenow={row?.progress ?? 8}
                                className="h-full rounded-full bg-linear-to-r from-[#EA83A5] via-[#9B52FF] to-[#6830F4] transition-[width] duration-500 ease-out"
                                style={{ width: `${row?.progress ?? 8}%` }}
                                data-detail-replica-reverse-progress={index}
                              />
                            )}
                          </div>
                          {status === "processing" && (
                            <span className="mt-2 inline-flex h-7 items-center rounded-full bg-linear-to-r from-[#B254D8] to-[#6830F4] px-3 text-xs text-white">
                              AI反推中
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            {reverseModalHasBottomOverflow && (
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-16 bg-[linear-gradient(to_bottom,rgba(17,18,21,0)_0%,rgba(17,18,21,.45)_55%,rgba(17,18,21,.9)_100%)]"
                data-detail-replica-reverse-bottom-fade
              />
            )}
          </div>

          <div
            data-detail-replica-reverse-footer
            className="mt-2 shrink-0 rounded-[12px] bg-[#161616] px-4 py-[14px]"
          >
            <div className="flex items-end justify-between gap-5 max-lg:flex-col max-lg:items-stretch">
              <div
                data-detail-replica-reverse-controls
                className="flex min-w-0 flex-wrap items-end gap-5 max-lg:w-full max-sm:flex-col max-sm:items-stretch max-sm:gap-3"
              >
                <div
                  data-detail-replica-reverse-tabs-field
                  className="flex shrink-0 flex-col gap-[6px]"
                >
                  <span aria-hidden="true" className="h-5 leading-5">
                    &nbsp;
                  </span>
                  <EcomTabs
                    aria-label="反推提示词版本"
                    activeKey={state.imageStyle}
                    onChange={(value) => {
                      updateReverseSetting(DETAIL_REPLICA_IMAGE_STYLE_KEY, value);
                      setColorOpen(false);
                    }}
                    items={PROMPT_STYLE_OPTIONS.map((option) => ({
                      key: option.value,
                      label: option.label,
                      disabled: reversing,
                    }))}
                    size="medium"
                    data-detail-replica-reverse-variant-switch
                  />
                </div>

                {state.imageStyle === "standard" && (
                  <div
                    data-detail-replica-reverse-text-settings
                    className="flex items-end gap-5 max-sm:w-full max-sm:flex-col max-sm:items-stretch max-sm:gap-3"
                  >
                    <div
                      data-detail-replica-reverse-color-field
                      className="flex flex-col gap-[6px] max-sm:w-full"
                    >
                      <span className="flex h-5 items-center text-[12px] leading-5 text-[#A0A0A0]">颜色</span>
                      <EcomPopover
                        open={colorOpen}
                        onOpenChange={(open) => {
                          setColorOpen(open);
                        }}
                        trigger="click"
                        placement="topLeft"
                        arrow={false}
                        classNames={{ container: "ecom-detail-replica-color-popover" }}
                        content={(
                          <div className="flex w-[216px] items-stretch gap-2">
                            <EcomButton
                              type="button"
                              variant="ghost"
                              size="mini"
                              disabled={!state.fontColor || reversing}
                              onClick={() => updateReverseSetting(DETAIL_REPLICA_FONT_COLOR_KEY, "")}
                              aria-label="不选颜色"
                              title="不选颜色"
                            >
                              <Ban className="h-3.5 w-3.5" />
                            </EcomButton>
                            <div className="min-w-0 flex-1">
                              <HsvColorPicker
                                value={state.fontColor}
                                onChange={(color) => updateReverseSetting(DETAIL_REPLICA_FONT_COLOR_KEY, color)}
                              />
                            </div>
                          </div>
                        )}
                      >
                        <EcomButton
                          type="button"
                          variant="outline"
                          size="medium"
                          disabled={reversing}
                          className="max-sm:flex-1"
                        >
                          <span
                            className={cn(
                              "flex h-[26px] w-[26px] items-center justify-center rounded-[2px] border border-[#3F3F3F]",
                              !state.fontColor && "bg-transparent",
                            )}
                            style={state.fontColor ? { backgroundColor: normalizeHexColor(state.fontColor) } : undefined}
                          >
                            {!state.fontColor && <Ban className="h-3.5 w-3.5" />}
                          </span>
                          <span>{state.fontColor ? normalizeHexColor(state.fontColor) : "不限颜色"}</span>
                        </EcomButton>
                      </EcomPopover>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-col items-end gap-[6px] max-lg:w-full">
                <div
                  data-detail-replica-reverse-costs
                  className={cn(
                    "grid h-5 items-baseline gap-5 leading-5",
                    hasReverseResults ? "grid-cols-[165px_165px]" : "grid-cols-[165px]",
                  )}
                >
                  <div className="flex w-[165px] items-baseline justify-between text-xs text-[#A0A0A0]">
                    <span>{state.references.length}/{state.references.length}预估消耗</span>
                    <span className="flex items-baseline">
                      {unitCost.cost > 0 && unitCost.cost < unitCost.original && (
                        <span className="text-xs text-[#A0A0A0] line-through">{reverseEstimateOriginal}</span>
                      )}
                      <span
                        className={cn(
                          unitCost.cost > 0 && unitCost.cost < unitCost.original ? "ml-2" : "",
                          "text-lg font-medium leading-5 text-[#FCB453]",
                        )}
                        data-detail-replica-reverse-estimate
                      >
                        {reverseEstimateCost}
                      </span>
                      <span className="ml-1 text-xs text-[#A0A0A0]">豌豆</span>
                    </span>
                  </div>
                  {hasReverseResults && generateEstimate && (
                    <div className="flex w-[165px] items-baseline justify-between text-xs text-[#A0A0A0]">
                      <span>{state.references.length}/{state.references.length}预估消耗</span>
                      <span className="flex items-baseline">
                        {generateEstimate.hasDiscount && (
                          <span className="text-xs text-[#A0A0A0] line-through">
                            {generateEstimate.originalCost}
                          </span>
                        )}
                        <span
                          className={cn(
                            generateEstimate.hasDiscount ? "ml-2" : "",
                            "text-lg font-medium leading-5 text-[#FCB453]",
                          )}
                          data-detail-replica-generate-estimate
                        >
                          {generateEstimate.cost}
                        </span>
                        <span className="ml-1 text-xs text-[#A0A0A0]">豌豆</span>
                      </span>
                    </div>
                  )}
                </div>
                <div
                  data-detail-replica-reverse-actions
                  className={cn(
                    "items-center justify-end max-sm:w-full max-sm:gap-2",
                    hasReverseResults
                      ? "grid grid-cols-[165px_165px] gap-5 max-sm:flex"
                      : "grid grid-cols-[165px] justify-items-start max-sm:flex",
                  )}
                >
                  {reversing ? (
                    hasReverseResults ? (
                      <>
                        <EcomButton
                          type="button"
                          size="medium"
                          disabled
                          className="w-full max-sm:flex-1"
                        >
                          重新反推提示词
                        </EcomButton>
                        <EcomButton
                          type="button"
                          size="medium"
                          disabled
                          className="w-full max-sm:flex-1"
                        >
                          开始生图
                        </EcomButton>
                      </>
                    ) : (
                      <EcomButton
                        type="button"
                        size="medium"
                        disabled
                        className="w-full max-sm:flex-1"
                      >
                        反推提示词
                      </EcomButton>
                    )
                  ) : hasReverseResults ? (
                    <>
                      <EcomButton
                        type="button"
                        size="medium"
                        onClick={() => void runReverseAll()}
                        className="w-full max-sm:flex-1"
                      >
                        重新反推提示词
                      </EcomButton>
                      <EcomButton
                        type="button"
                        size="medium"
                        onClick={() => {
                          setModalOpen(false);
                          onGenerate?.(state.references.length);
                        }}
                        className="w-full max-sm:flex-1"
                      >
                        开始生图
                      </EcomButton>
                    </>
                  ) : (
                    <EcomButton
                      type="button"
                      size="medium"
                      variant={gate.settingsReady ? "default" : "outline"}
                      onClick={() => void runReverseAll()}
                      data-ready={gate.settingsReady ? "true" : "false"}
                      className="w-full max-sm:flex-1"
                    >
                      反推提示词
                    </EcomButton>
                  )}
                </div>
              </div>
            </div>
          </div>
      </EcomModal>
    </>
  );
}

function ModalThumb({ url, label }: { url: string; label: string }) {
  return (
    <div className="relative h-[60px] w-[60px] shrink-0 overflow-hidden rounded-[4px] bg-white/8">
      {/* eslint-disable-next-line @next/next/no-img-element -- Reverse draft images can be external dynamic URLs. */}
      <img src={url} alt={label} className="h-full w-full object-cover" />
      <span className="absolute inset-x-0 bottom-0 bg-black/40 py-0.5 text-center text-[10px] text-white/85">{label}</span>
    </div>
  );
}

function ScreenUploadField({
  kind,
  images,
  disabled,
  uploading,
  onFiles,
  onRemove,
  onReorder,
  onHoverPreviewChange,
  onEdit,
}: {
  kind: "product" | "model";
  images: string[];
  disabled: boolean;
  uploading: boolean;
  onFiles: (files: FileList | File[]) => void;
  onRemove: (index: number) => void;
  onReorder: (images: string[]) => void;
  onHoverPreviewChange?: (preview: { url: string; alt?: string } | null) => void;
  onEdit: (index: number) => void;
}) {
  const [dragOver, setDragOver] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [dragImages, setDragImages] = useState<string[] | null>(null);
  const [dragPreview, setDragPreview] = useState<{ url: string; x: number; y: number } | null>(null);
  const dragImagesRef = useRef<string[]>([]);
  const initialImagesRef = useRef<string[]>([]);
  const activeDragIndexRef = useRef<number | null>(null);
  const dragActivatedRef = useRef(false);
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);
  const pointerIdRef = useRef<number | null>(null);
  const pointerTargetRef = useRef<HTMLDivElement | null>(null);
  const dragCleanupRef = useRef<(() => void) | null>(null);
  const displayedImages = dragImages || images;
  const uploadItems: ImageUploadFieldItem[] = displayedImages.map((url, index) => ({
    id: `${kind}-${index}-${url}`,
    src: url,
    alt: `${kind === "product" ? "产品图" : "模特图"} ${index + 1}`,
    status: url.startsWith("blob:") ? "uploading" : "ready",
    itemAttributes: {
      "data-detail-replica-upload-item": `${kind}-${index + 1}`,
      draggable: false,
      onPointerDown: (event) => handleItemDragStart(event, index),
    },
  }));

  const handleFiles = (files: FileList | null) => {
    if (!disabled && files?.length) onFiles(files);
  };

  const clearUploadHoverPreview = () => {
    onHoverPreviewChange?.(null);
  };

  const resetReorderDrag = () => {
    dragCleanupRef.current?.();
    dragCleanupRef.current = null;
    const pointerTarget = pointerTargetRef.current;
    const pointerId = pointerIdRef.current;
    if (pointerTarget && pointerId != null && pointerTarget.hasPointerCapture(pointerId)) {
      pointerTarget.releasePointerCapture(pointerId);
    }
    dragActivatedRef.current = false;
    pointerStartRef.current = null;
    pointerIdRef.current = null;
    pointerTargetRef.current = null;
    activeDragIndexRef.current = null;
    dragImagesRef.current = [];
    initialImagesRef.current = [];
    setDraggedIndex(null);
    setDragOverIndex(null);
    setDragImages(null);
    setDragPreview(null);
  };

  const handleItemDrop = (index: number) => {
    const activeIndex = activeDragIndexRef.current;
    if (!dragActivatedRef.current || activeIndex == null || activeIndex === index) return;
    const nextImages = [...dragImagesRef.current];
    const [movedImage] = nextImages.splice(activeIndex, 1);
    nextImages.splice(index, 0, movedImage);
    dragImagesRef.current = nextImages;
    activeDragIndexRef.current = index;
    setDraggedIndex(index);
    setDragOverIndex(index);
    setDragImages(nextImages);
  };

  const getDropIndexAtPointer = (x: number, y: number) => {
    const dropItem = document.elementFromPoint(x, y)
      ?.closest<HTMLElement>("[data-detail-replica-upload-item]");
    const identifier = dropItem?.dataset.detailReplicaUploadItem;
    const prefix = `${kind}-`;
    if (!identifier?.startsWith(prefix)) return null;

    const index = Number.parseInt(identifier.slice(prefix.length), 10) - 1;
    return Number.isInteger(index) && index >= 0 && index < dragImagesRef.current.length
      ? index
      : null;
  };

  const handleItemDragStart = (event: ReactPointerEvent<HTMLDivElement>, index: number) => {
    if (
      disabled
      || uploading
      || event.button !== 0
      || (event.target as HTMLElement).closest("button,input,label")
    ) return;
    event.preventDefault();
    clearUploadHoverPreview();
    resetReorderDrag();

    const startingImages = [...images];
    const startingURL = startingImages[index];
    initialImagesRef.current = startingImages;
    activeDragIndexRef.current = index;
    pointerStartRef.current = { x: event.clientX, y: event.clientY };
    pointerIdRef.current = event.pointerId;
    pointerTargetRef.current = event.currentTarget;
    event.currentTarget.setPointerCapture(event.pointerId);

    const handlePointerMove = (pointerEvent: PointerEvent) => {
      if (pointerEvent.pointerId !== pointerIdRef.current) return;
      if (!dragActivatedRef.current) {
        if (
          pointerStartRef.current == null
          || (
            pointerEvent.clientX === pointerStartRef.current.x
            && pointerEvent.clientY === pointerStartRef.current.y
          )
        ) return;
        dragActivatedRef.current = true;
        dragImagesRef.current = startingImages;
        setDragImages(startingImages);
        setDraggedIndex(index);
        setDragOverIndex(index);
        setDragPreview({ url: startingURL, x: pointerEvent.clientX, y: pointerEvent.clientY });
      } else {
        setDragPreview((current) => current
          ? { ...current, x: pointerEvent.clientX, y: pointerEvent.clientY }
          : null);
      }
      const dropIndex = getDropIndexAtPointer(pointerEvent.clientX, pointerEvent.clientY);
      if (dropIndex != null) handleItemDrop(dropIndex);
    };
    const finishDrag = (cancelled: boolean) => {
      window.removeEventListener("pointermove", handlePointerMove, true);
      window.removeEventListener("pointerup", handlePointerUp, true);
      window.removeEventListener("pointercancel", handlePointerCancel, true);
      dragCleanupRef.current = null;
      if (dragActivatedRef.current && !cancelled) {
        const finalImages = dragImagesRef.current;
        const changed = finalImages.some((url, imageIndex) => url !== initialImagesRef.current[imageIndex]);
        if (changed) onReorder(finalImages);
      }
      resetReorderDrag();
    };
    const handlePointerUp = (pointerEvent: PointerEvent) => {
      if (pointerEvent.pointerId === pointerIdRef.current) finishDrag(false);
    };
    const handlePointerCancel = (pointerEvent: PointerEvent) => {
      if (pointerEvent.pointerId === pointerIdRef.current) finishDrag(true);
    };
    dragCleanupRef.current = () => {
      window.removeEventListener("pointermove", handlePointerMove, true);
      window.removeEventListener("pointerup", handlePointerUp, true);
      window.removeEventListener("pointercancel", handlePointerCancel, true);
    };
    window.addEventListener("pointermove", handlePointerMove, true);
    window.addEventListener("pointerup", handlePointerUp, true);
    window.addEventListener("pointercancel", handlePointerCancel, true);
  };

  useEffect(() => () => resetReorderDrag(), []);

  const emptyState = disabled && kind === "product" ? (
    <div className="flex h-[104px] items-center justify-center rounded-[8px] border border-dashed border-[#686868] bg-[#141414] text-[#D7D9DB]">
      <Ban className="h-6 w-6" />
    </div>
  ) : (
    <label className={disabled ? "cursor-not-allowed" : "cursor-pointer"}>
      <input
        className="sr-only"
        type="file"
        accept={IMAGE_ACCEPT}
        multiple
        disabled={disabled || uploading}
        onChange={(event) => {
          handleFiles(event.target.files);
          event.currentTarget.value = "";
        }}
      />
      <ImageUploadEmptyState
        uploading={uploading}
        dragOver={dragOver}
        multiple
        formats={["JPG", "PNG"]}
        bgClassName="bg-[#141414]"
        className="h-[104px] rounded-[8px] border-[#686868]"
      />
    </label>
  );

  const addTrigger = (
    <label className={disabled ? "cursor-not-allowed" : "cursor-pointer"}>
      <input
        className="sr-only"
        type="file"
        accept={IMAGE_ACCEPT}
        multiple
        disabled={disabled || uploading}
        onChange={(event) => {
          handleFiles(event.target.files);
          event.currentTarget.value = "";
        }}
      />
      <ImageUploadAddTrigger uploading={uploading} className="rounded-[4px]" />
    </label>
  );

  return (
    <>
      <ImageUploadField
        items={uploadItems}
        onPasteFiles={handleFiles}
        pasteDisabled={disabled || uploading || draggedIndex != null}
        maxItems={Number.MAX_SAFE_INTEGER}
        dragOver={dragOver}
        emptyState={emptyState}
        addTrigger={addTrigger}
        capabilities={{
          activate: false,
          remove: true,
          edit: true,
          reorder: true,
          showNumber: false,
          showReference: false,
        }}
        labels={{
          remove: (_item, index) => `删除${kind === "product" ? "产品图" : "模特图"} ${index + 1}`,
          edit: (_item, index) => `裁剪${kind === "product" ? "产品图" : "模特图"} ${index + 1}`,
          add: `添加${kind === "product" ? "产品图" : "模特图"}`,
        }}
        editIcon={<CropIcon data-detail-replica-upload-crop className="size-2.5 text-white" />}
        onItemRemove={(_item, index) => onRemove(index)}
        onItemEdit={(_item, index) => onEdit(index)}
        onItemHoverChange={(item) => {
          if (dragActivatedRef.current && item) return;
          onHoverPreviewChange?.(item ? { url: item.src, alt: item.alt } : null);
        }}
        isItemRemoveDisabled={() => uploading || draggedIndex != null}
        isItemEditDisabled={() => uploading || draggedIndex != null}
        reorder={{
          activeIndex: draggedIndex,
          overIndex: dragOverIndex,
        }}
        gridClassName="grid-cols-3 border-0 bg-transparent p-0"
        itemClassName="touch-none select-none rounded-[4px]"
        imageClassName="object-cover"
        className={disabled ? "cursor-not-allowed opacity-70" : ""}
        onDrop={(event) => {
          event.preventDefault();
          setDragOver(false);
          if (!disabled && !uploading && draggedIndex == null) handleFiles(event.dataTransfer.files);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled && !uploading && draggedIndex == null) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
      />
      {dragPreview && typeof document !== "undefined" && createPortal(
        <div
          data-detail-replica-upload-drag-overlay={kind}
          className="pointer-events-none fixed z-[500] h-[118px] w-[84px] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-[4px] bg-[#111]"
          style={{ left: dragPreview.x, top: dragPreview.y }}
        >
          {/* eslint-disable-next-line @next/next/no-img-element -- Pointer overlay previews a dynamic uploaded URL. */}
          <img
            src={dragPreview.url}
            alt=""
            className="h-full w-full object-contain"
            draggable={false}
          />
        </div>,
        document.body,
      )}
    </>
  );
}
